/**
 * Mietwagen — Shared utility functions for the rental car module
 *
 * Pure functions for Akten creation, invoice calculations, and field configurations.
 * Used by NeueAkteModal, MietwagenRechnungGenerator, and process step sections.
 */

// ── Akten-Nummer Prefix ──

const BETRIEB_PREFIX: Record<string, string> = {
  gutachten: 'GA',
  autoglas: 'AG',
  mietwagen: 'MW',
  werkstatt: 'WK',
  protex: 'PX',
  unfallgiganten: 'UG',
  airbnb: 'AB',
  recht: 'RA',
};

export function getAktenNummerPrefix(betriebId: string): string {
  return BETRIEB_PREFIX[betriebId] || 'XX';
}

export function generiereAktenNummer(betriebId: string, jahr: number, zaehler: number): string {
  const prefix = getAktenNummerPrefix(betriebId);
  return `${prefix}-${jahr}-${String(zaehler).padStart(4, '0')}`;
}

// ── Mietdauer Berechnung ──

export function berechneMietdauer(von: string, bis: string): number {
  if (!von || !bis) return 0;
  const d1 = new Date(von);
  const d2 = new Date(bis);
  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
  return Math.max(1, Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1);
}

// ── Rechnung Berechnung ──

export interface MietwagenPosition {
  beschreibung: string;
  menge: number;
  einheit: string;
  einzelpreis: number;
  gesamt: number;
}

export interface MietwagenRechnungBerechnung {
  netto: number;
  mwst_satz: number;
  mwst_betrag: number;
  brutto: number;
}

export function berechneRechnungsSummen(
  positionen: MietwagenPosition[],
  mwstSatz: number = 19,
): MietwagenRechnungBerechnung {
  const netto = positionen.reduce((sum, p) => sum + p.gesamt, 0);
  const mwst_betrag = Math.round(netto * (mwstSatz / 100) * 100) / 100;
  const brutto = Math.round((netto + mwst_betrag) * 100) / 100;
  return {
    netto: Math.round(netto * 100) / 100,
    mwst_satz: mwstSatz,
    mwst_betrag,
    brutto,
  };
}

export function erstelleStandardPositionen(
  mietTage: number,
  tagessatz: number,
): MietwagenPosition[] {
  const mietkosten = mietTage * tagessatz;
  return [{
    beschreibung: `Mietwagenkosten Unfallersatzfahrzeug (${mietTage} Tage × ${tagessatz.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €/Tag)`,
    menge: mietTage,
    einheit: 'Tage',
    einzelpreis: tagessatz,
    gesamt: mietkosten,
  }];
}

// ── Mietwagen Akte Insert Data ──

export interface MietwagenAkteFormData {
  kundeName: string;
  kennzeichen: string;
  versicherung?: string;
  schadennummer?: string;
  mietbeginn?: string;
  mietende?: string;
  tagespreis?: number;
  schadendatum?: string;
  quelle?: string;
}

export function erstelleMietwagenInsertData(
  formData: MietwagenAkteFormData,
  aktenNr: string,
  aktenNummer: string,
) {
  const insertData: Record<string, any> = {
    akten_nr: aktenNr,
    akten_nummer: aktenNummer,
    business_unit: 'mietwagen',
    titel: `Mietwagen — ${formData.kennzeichen.toUpperCase()}`,
    status: 'neu',
    prioritaet: 'mittel',
    kunde_name: formData.kundeName,
    kennzeichen: formData.kennzeichen.toUpperCase(),
    created_via: 'manuell',
  };

  if (formData.versicherung?.trim()) insertData.versicherung = formData.versicherung.trim();
  if (formData.schadennummer?.trim()) insertData.schadennummer = formData.schadennummer.trim();
  if (formData.mietbeginn) insertData.mietbeginn = formData.mietbeginn;
  if (formData.mietende) insertData.mietende = formData.mietende;
  if (formData.tagespreis != null && formData.tagespreis > 0) insertData.tagespreis = formData.tagespreis;
  if (formData.schadendatum) insertData.schadendatum = formData.schadendatum;
  if (formData.quelle?.trim()) insertData.erteilt_durch = formData.quelle.trim();

  return insertData;
}

// ── Festschreibung (Invoice Finalization) ──

export interface FestschreibungData {
  rechnungs_nr: string;
  business_unit: 'mietwagen';
  akte_id: string;
  betreff: string;
  empfaenger_firma: string | null;
  empfaenger_name: string | null;
  empfaenger_strasse: string | null;
  empfaenger_plz: string | null;
  empfaenger_ort: string | null;
  netto_betrag: number;
  mwst_satz: number;
  mwst_betrag: number;
  brutto_betrag: number;
  bezahlt_betrag: number;
  kuerzung_betrag: number;
  status: 'gesendet';
  rechnungsdatum: string;
  leistungsdatum: string | null;
  faellig_am: string | null;
  versicherung: string | null;
  schadensnummer: string | null;
  kennzeichen: string | null;
  notizen: string | null;
}

// ── Mietvertrag Storage Pfad ──

export function getMietvertragStoragePath(
  akte: { id?: string | null; akten_nummer?: string | null },
  filename: string,
): string {
  const aktenNr = akte.akten_nummer || akte.id;
  return `${aktenNr}/Mietvertrag/${filename}`;
}

// ── Übergabe-Protokoll Storage Pfade ──

export function getUebergabeFotoStoragePath(
  akte: { id?: string | null; akten_nummer?: string | null },
  filename: string,
): string {
  const aktenNr = akte.akten_nummer || akte.id;
  return `${aktenNr}/Uebergabe-Fotos/${filename}`;
}

// ── Rückgabeprotokoll Storage Pfade ──

export function getRueckgabeStoragePath(
  akte: { id?: string | null; akten_nummer?: string | null },
  filename: string,
): string {
  const aktenNr = akte.akten_nummer || akte.id;
  return `${aktenNr}/Rueckgabeprotokoll/${filename}`;
}

export function getRueckgabeFotoStoragePath(
  akte: { id?: string | null; akten_nummer?: string | null },
  filename: string,
): string {
  const aktenNr = akte.akten_nummer || akte.id;
  return `${aktenNr}/Rueckgabeprotokoll/fotos/${filename}`;
}

// ── Rechnungs-Bemerkung ──

export function erstelleRechnungsBemerkung(
  akte: { kunde_name?: string | null; akten_nummer?: string | null; schadennummer?: string | null },
): string {
  const kunde = akte.kunde_name || '—';
  const aktenNr = akte.akten_nummer || '—';
  const schadenNr = akte.schadennummer || '—';
  return `Geschädigter: ${kunde}\nAktenzeichen: ${aktenNr}\nSchadennummer: ${schadenNr}`;
}

export function erstelleFestschreibungData(
  rechnungsnr: string,
  akteId: string,
  empfaenger: { firma: string; name: string; strasse: string; plz_ort: string; schadennr: string },
  rechnung: { rechnungsdatum: string; leistungszeitraum_von: string; leistungszeitraum_bis: string; faellig_am: string; netto: number; mwst_satz: number; mwst_betrag: number; brutto: number; bemerkung: string },
  akteData: { kennzeichen?: string; versicherung?: string; schadennummer?: string },
): FestschreibungData {
  // Split plz_ort into plz and ort
  const plzOrtParts = empfaenger.plz_ort?.trim().split(/\s+/) || [];
  const plz = plzOrtParts[0] || null;
  const ort = plzOrtParts.slice(1).join(' ') || null;

  return {
    rechnungs_nr: rechnungsnr,
    business_unit: 'mietwagen',
    akte_id: akteId,
    betreff: `Mietwagenrechnung ${rechnungsnr}`,
    empfaenger_firma: empfaenger.firma || null,
    empfaenger_name: empfaenger.name || null,
    empfaenger_strasse: empfaenger.strasse || null,
    empfaenger_plz: plz,
    empfaenger_ort: ort,
    netto_betrag: rechnung.netto,
    mwst_satz: rechnung.mwst_satz,
    mwst_betrag: rechnung.mwst_betrag,
    brutto_betrag: rechnung.brutto,
    bezahlt_betrag: 0,
    kuerzung_betrag: 0,
    status: 'gesendet',
    rechnungsdatum: rechnung.rechnungsdatum,
    leistungsdatum: rechnung.leistungszeitraum_von || null,
    faellig_am: rechnung.faellig_am || null,
    versicherung: akteData.versicherung || null,
    schadensnummer: akteData.schadennummer || null,
    kennzeichen: akteData.kennzeichen || null,
    notizen: rechnung.bemerkung || null,
  };
}
